import type { MentionBoostInsightsDto } from '@fluenceur/types';
import { resolveInsightsView } from '@fluenceur/types';

export { resolveInsightsView } from '@fluenceur/types';

/**
 * A boost is in its consolidating window while Meta is still attributing and
 * stabilising figures. The backend owns the cutoff (first ~72h after launch) and
 * sets `dataState`; the frontend never recomputes it from timestamps.
 */
export function isConsolidating(insights: Pick<MentionBoostInsightsDto, 'dataState'>): boolean {
  return insights.dataState === 'consolidating';
}

export function hasNoData(insights: Pick<MentionBoostInsightsDto, 'dataState'>): boolean {
  return insights.dataState === 'no_data';
}

const PLACEHOLDER = '--';

// Currency is rendered through the shared `Money` component (Chakra
// `FormatNumber`), not a string formatter here, so every price reads like the
// rest of the app. A null money value renders the placeholder at the call site.

/** Compact integer (impressions, clicks, purchases). Null → placeholder. */
export function formatCount(value: number | null, locale: string): string {
  if (value === null || Number.isNaN(value)) {
    return PLACEHOLDER;
  }
  return new Intl.NumberFormat(locale, { maximumFractionDigits: 0 }).format(value);
}

/** A percentage already expressed in percent units (e.g. 1.8 → "1.8%"). */
export function formatPercent(value: number | null, locale: string): string {
  if (value === null || Number.isNaN(value)) {
    return PLACEHOLDER;
  }
  return `${new Intl.NumberFormat(locale, { maximumFractionDigits: 2 }).format(value)}%`;
}

/** A multiplier (ROAS, frequency) rendered as "2.4x". Null → placeholder. */
export function formatRatio(value: number | null, locale: string): string {
  if (value === null || Number.isNaN(value)) {
    return PLACEHOLDER;
  }
  return `${new Intl.NumberFormat(locale, { maximumFractionDigits: 1 }).format(value)}x`;
}

export interface InsightsHeadlineMetric {
  key: string;
  /** i18n key, translated by the caller so this stays free of the i18n runtime. */
  labelKey: string;
  value: string;
}

/**
 * The two headline figures a boost is judged on, picked by objective: a
 * conversion boost leads with ROAS then purchases, a traffic one with click
 * rate then clicks. Single source of truth for the pair, so the table cell and
 * the live-rail card can never show a different couple, in a different order,
 * for the same boost.
 *
 * Callers still own the `no_data` branch: an absent dataset is a rendering
 * decision (a muted hint, not two placeholders), not a formatting one.
 */
export function buildInsightsHeadlineMetrics(
  insights: Pick<
    MentionBoostInsightsDto,
    'recommendedView' | 'roas' | 'purchases' | 'ctr' | 'clicks'
  >,
  locale: string
): InsightsHeadlineMetric[] {
  if (resolveInsightsView(insights) === 'conversion') {
    return [
      {
        key: 'roas',
        labelKey: 'mentions.boost.insights.metrics.roas',
        value: formatRatio(insights.roas, locale),
      },
      {
        key: 'purchases',
        labelKey: 'mentions.boost.insights.metrics.purchases',
        value: formatCount(insights.purchases, locale),
      },
    ];
  }

  return [
    {
      key: 'ctr',
      labelKey: 'mentions.boost.insights.metrics.ctr',
      value: formatPercent(insights.ctr, locale),
    },
    {
      key: 'clicks',
      labelKey: 'mentions.boost.insights.metrics.clicks',
      value: formatCount(insights.clicks, locale),
    },
  ];
}

/**
 * Locale-aware "last synced" date for the attribution line, or null when the
 * boost never synced or the timestamp is unparseable. Keeps the freshness label
 * honest: no fabricated date when there is nothing to show.
 */
export function formatLastSynced(iso: string | null, locale: string): string | null {
  if (!iso) {
    return null;
  }
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) {
    return null;
  }
  try {
    return new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeStyle: 'short' }).format(
      date
    );
  } catch {
    return date.toISOString();
  }
}
